import { Injectable, inject } from '@angular/core';
import { Activity } from '@omni/classes/activity/activity.class';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { EmailActivity, EmailStatusCodes } from '@omni/classes/activity/email.activity.class';
import { SampleActivity } from '@omni/classes/activity/sample.activity.class';
import { FollowUpActivity } from '@omni/classes/activity/follow-up-action.activity.class';
import { CaseActivity } from '@omni/classes/case-intake/case-activity.class';
import { CaseCategory } from '@omni/classes/case-intake/case-utility.class';
import { EmailTemplateType } from '@omni/classes/email-templates/email-template.class';
import { CustomerAssessService } from '@omni/services/customer-assess/customer-assess.service';
import { MeetingStructureService } from '@omni/services/meeting-structure/meeting-structure.service';
import { SurveyStatus } from '@omni/classes/customer-assessment/assessment-template.class';
import { SdrStatus } from '@omni/classes/sample/sample-delivery-request.class';
import { SampleDeliveryRequestService } from '@omni/services/sample/sample-delivery-request.service';
import { MeetingExpenseService } from '@omni/services/meeting-expense/meeting-expense.service';
import { MandatoryInlineActivityRule } from '@omni/interfaces/meeting/mandatory-format-activities.interface';
import { MeetingMandatoryActivitiesConfigService } from './meeting-mandatory-activities-config.service';
import { isActivityLinkedToMeetingByIds, sampleOrderLinkedToMeeting } from '@omni/services/meeting/meeting-activity-link.util';

export const MANDATORY_INLINE_ACTIVITY_TYPE = {
  SAMPLE_DROP: 'SampleDrop',
  SAMPLE_DELIVERY_REQUEST: 'SampleDeliveryRequest',
  // Matches MandatoryInlineActivityRule.activityType's documented value —
  // must stay in sync with the same string activities-details-pane.ts's
  // isPromoMaterialSectionVisible passes to shouldHideInlineActivityForFormat.
  PROMOTIONAL_MATERIAL: 'PromotionalMaterial',
  MESSAGE: 'Message',
  FOLLOW_UP: 'FollowUp',
  CUSTOMER_INQUIRY: 'CustomerInquiry',
  MEDICAL_INQUIRY: 'MedicalInquiry',
  SURVEY: 'Survey',
  EXPENSE: 'Expense',
} as const;

/**
 * Blocks meeting complete when per-format rules are not met (OMNI-59153).
 * Uses same JSON as BU IO `BU_APPOINTMENT_GA_FORMAT_MANDATORY_INLINE_ACTIVITIES`.
 */
@Injectable({ providedIn: 'root' })
export class MeetingMandatoryActivitiesValidationService {
  private readonly config = inject(MeetingMandatoryActivitiesConfigService);
  private readonly meetingStructure = inject(MeetingStructureService);
  private readonly customerAssess = inject(CustomerAssessService);
  private readonly sdrService = inject(SampleDeliveryRequestService);
  private readonly expenseService = inject(MeetingExpenseService);

  canCompleteMeeting(appointment: AppointmentActivity): { ok: boolean } {
    const formatId = appointment?.indskr_activitytype;
    if (!formatId) {
      return { ok: true };
    }
    const doc = this.config.getDocument();
    if (!doc?.formats?.length) {
      return { ok: true };
    }
    const entry = doc.formats.find(f => f.formatId === formatId);
    if (!entry?.mandatoryInlineActivities?.length) {
      return { ok: true };
    }
    const linked = this.meetingStructure.getLinkedInMeetingActivitiesForFormatValidation(appointment);
    const surveysForMeeting = (this.customerAssess.allContactSurveysByAssessment || []).filter(
      a =>
        isActivityLinkedToMeetingByIds(
          [a['_indskr_appointment_value'], a['indskr_appointmentid'], a['indskr_appointment']],
          appointment
        )
    );
    for (const rule of entry.mandatoryInlineActivities) {
      if (!this.isRuleSatisfied(rule, linked, surveysForMeeting, appointment)) {
        return { ok: false };
      }
    }
    return { ok: true };
  }

  private isRuleSatisfied(
    rule: MandatoryInlineActivityRule,
    linked: Activity[],
    surveys: any[],
    appointment: AppointmentActivity
  ): boolean {
    const t = rule.activityType;
    const st = (rule.requiredStatus || '').toLowerCase();

    switch (t) {
      case MANDATORY_INLINE_ACTIVITY_TYPE.SAMPLE_DROP: {
        const matches = (s: SampleActivity) =>
          this.isSampleLinkedToMeeting(s, appointment) && this.statusMatches(s, st, 'sample');
        return linked.filter(a => a instanceof SampleActivity && !(a as SampleActivity).isPromoMaterial).some(s => matches(s as SampleActivity));
      }
      case MANDATORY_INLINE_ACTIVITY_TYPE.PROMOTIONAL_MATERIAL: {
        // Same entity/lifecycle as Sample Drop (SampleActivity), distinguished
        // only by the isPromoMaterial flag — reuses the identical link/status
        // matching rather than a parallel implementation.
        const matches = (s: SampleActivity) =>
          this.isSampleLinkedToMeeting(s, appointment) && this.statusMatches(s, st, 'sample');
        return linked.filter(a => a instanceof SampleActivity && (a as SampleActivity).isPromoMaterial).some(s => matches(s as SampleActivity));
      }
      case MANDATORY_INLINE_ACTIVITY_TYPE.SAMPLE_DELIVERY_REQUEST: {
        // Deny-list semantics, deliberately NOT driven by rule.requiredStatus (OMNI-61560):
        // BU-authored requiredStatus values for this activity type have proven unreliable
        // (e.g. "Completed", which no SdrStatus member is ever labeled — see SdrStatus's
        // own doc comment), so an allow-list naming one required status silently blocked
        // completion for every SDR regardless of its real status. rule.blockingStatuses
        // (product-team-agreed replacement) instead names the statuses that SHOULD block,
        // entirely BU-config-driven — no status list is hardcoded here; the BU decides
        // which statuses (e.g. just "Active", a still-open unsubmitted Draft) block
        // completion, and any status not listed is non-blocking. A scrapped Draft
        // (Inactive) is excluded from consideration entirely below rather than counted
        // as clearing the block.
        const blockingStatusCodes = this.resolveSdrBlockingStatusCodes(rule.blockingStatuses);
        return (this.sdrService.allSdrs || [])
          .filter(sdr => sdr.statusCode !== SdrStatus.Inactive)
          .filter(sdr => isActivityLinkedToMeetingByIds([sdr.appointmentId], appointment))
          .some(sdr => !blockingStatusCodes.has(sdr.statusCode));
      }
      case MANDATORY_INLINE_ACTIVITY_TYPE.MESSAGE: {
        return linked
          .filter(
            a =>
              a instanceof EmailActivity &&
              (a as EmailActivity).emailType != EmailTemplateType.RemoteURL
          )
          .some(m => this.statusMatches(m, st, 'email'));
      }
      case MANDATORY_INLINE_ACTIVITY_TYPE.FOLLOW_UP: {
        return linked.filter(a => a instanceof FollowUpActivity).some(fu =>
          this.statusMatches(fu, st, 'followup')
        );
      }
      case MANDATORY_INLINE_ACTIVITY_TYPE.CUSTOMER_INQUIRY: {
        return linked
          .filter(a => a instanceof CaseActivity && a.indskr_casecategory === CaseCategory.COMMERCIAL)
          .some(c => this.statusMatches(c, st, 'case'));
      }
      case MANDATORY_INLINE_ACTIVITY_TYPE.MEDICAL_INQUIRY: {
        return linked
          .filter(a => a instanceof CaseActivity && a.indskr_casecategory === CaseCategory.MEDICAL)
          .some(c => this.statusMatches(c, st, 'case'));
      }
      case MANDATORY_INLINE_ACTIVITY_TYPE.SURVEY: {
        return surveys.some(svy => this.surveyMatches(svy, st));
      }
      case MANDATORY_INLINE_ACTIVITY_TYPE.EXPENSE:
        // Bare existence check like SDR's deny-list: any status counts, incomplete Drafts included; scrapped ones do not.
        return this.expenseService.satisfiesMandatoryRule(appointment.ID);
      default:
        return true;
    }
  }

  private isSampleLinkedToMeeting(s: SampleActivity, m: AppointmentActivity): boolean {
    return sampleOrderLinkedToMeeting(s, m);
  }

  /**
   * Parses `rule.blockingStatuses` (SdrStatus member names, case/space-insensitive) into
   * the numeric codes that should block meeting completion for a linked SDR. No fallback
   * list is baked in here — an omitted/empty config means nothing blocks on status alone
   * (a linked SDR of any status satisfies the rule; only "no linked SDR at all" still
   * blocks). The BU config is the sole source of truth for which statuses block. A name
   * that doesn't match any known SdrStatus member is logged and skipped rather than
   * silently blocking forever — the exact failure mode `requiredStatus: "Completed"` hit
   * (OMNI-61560).
   */
  private resolveSdrBlockingStatusCodes(blockingStatuses: string[] | undefined): Set<number> {
    const codes = new Set<number>();
    for (const raw of blockingStatuses || []) {
      const key = Object.keys(SdrStatus).find(
        k => isNaN(Number(k)) && k.toLowerCase() === (raw || '').replace(/\s+/g, '').toLowerCase()
      );
      if (key) {
        codes.add(SdrStatus[key as keyof typeof SdrStatus] as unknown as number);
      } else {
        console.warn(`[MeetingMandatoryActivitiesValidationService] SampleDeliveryRequest blockingStatuses: "${raw}" does not match any SdrStatus member — ignored.`);
      }
    }
    return codes;
  }

  private statusMatches(
    a: Activity,
    required: string,
    kind: 'sample' | 'email' | 'followup' | 'case'
  ): boolean {
    const r = (required || 'completed').toLowerCase();
    if (kind === 'sample') {
      if (r === 'sent') {
        return false;
      }
      if (r === 'completed') {
        if (a.isCompleted) {
          return true;
        }
        const ss = a.statusString;
        return typeof ss === 'string' && ss.toLowerCase() === 'completed';
      }
    }
    if (kind === 'email') {
      const e = a as EmailActivity;
      if (r === 'sent') {
        return e.status === EmailStatusCodes.Sent || e.status === 3;
      }
      if (r === 'completed' || r === 'read') {
        if (a.isCompleted) {
          return true;
        }
        return e.status === EmailStatusCodes.Completed || e.status === EmailStatusCodes.Read || a.state === 1;
      }
    }
    if (kind === 'followup') {
      if (r === 'submitted' || r === 'completed') {
        if (a.isCompleted) {
          return true;
        }
        const s = a.getStatusString;
        return typeof s === 'string' && s.toLowerCase() === 'completed';
      }
      return a.isCompleted;
    }
    if (kind === 'case') {
      const c = a as CaseActivity;
      if (r === 'completed' || r === 'resolved' || r === 'submitted') {
        if (a.isCompleted) {
          return true;
        }
        return (
          c.state === 1 ||
          c._case_status_value?.toLowerCase().indexOf('complete') >= 0 ||
          c._case_status_value?.toLowerCase().indexOf('resolve') >= 0
        );
      }
    }
    return a.state === 1 || a.getStatusString === 'Completed';
  }

  private surveyMatches(s: any, required: string): boolean {
    const r = (required || 'submitted').toLowerCase();
    const st = s.surveyStatus;
    if (r === 'submitted') {
      return st === SurveyStatus.SUBMITTED || st === 548910001 || (typeof st === 'string' && st.toLowerCase() === 'submitted');
    }
    if (r === 'completed') {
      return (
        st === SurveyStatus.SUBMITTED ||
        st === 548910001 ||
        s.internalSurveyStatus === 'completed' ||
        (s.surveyStatus == null && s.statecode === 1)
      );
    }
    return true;
  }
}
